import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { ActionButton } from 'jbc-front/components/ActionButton';
import { Copy } from 'jbc-front/components/icons';
import { Label } from 'jbc-front/components/presenters/form/Label';
import { Note } from 'jbc-front/components/presenters/ui/Note';

import { Section } from 'components/ui/Section';
import { FormSection } from 'components/form/FormSection';
import { Radio } from 'components/form/Radio';
import { Textarea } from 'components/form/Textarea';
import { FileField } from 'components/react-hook-form/FileField';
import { Schema } from '../schema';
import { DifferentAddress } from './DifferentAddress';
import { DifferentAndForeignAddress } from './DifferentAndForeignAddress';
import { FormField } from 'components/form/FormField';
import styles from './NextYear.scss';
import { useDefaultYear } from '../../../../hooks/useDefaultYear';

import { gql, useQuery } from '@apollo/client';
import { FilePreview } from 'components/feature/FilePreview';
import { AmountField } from 'components/react-hook-form/AmountField';

const IMAGE_SETTINGS = gql`
  query imageSettings {
    clientSetting {
      id
      requestImageUploadSettings {
        handicap
      }
    }
  }
`;

type NextYearProps = {
  index: number;
};

export const NextYear: FC<NextYearProps> = ({ index }) => {
  const {
    formState: { errors },
    register,
    watch,
    setValue,
    getValues,
    trigger
  } = useFormContext<Schema>();

  const { defaultYear } = useDefaultYear();

  const thisYearDependentTaxLaw = watch(`otherFamilies.${index}.thisYear.dependentTaxLaw`);
  const nextYearDependentTaxLaw = watch(`otherFamilies.${index}.nextYear.dependentTaxLaw`);
  const residenceStatus = watch(`otherFamilies.${index}.nextYear.yearlyInfo.residenceStatus`);
  const handicapType = watch(`otherFamilies.${index}.nextYear.yearlyInfo.handicapType`);
  const handicapImage = watch(`otherFamilies.${index}.nextYear.yearlyInfo.handicapImage`);
  const otherFamilyErrors = errors.otherFamilies?.[index];

  const { data, loading } = useQuery(IMAGE_SETTINGS, { variables: { defaultYear } });
  if (loading) return null;
  const {
    clientSetting: {
      requestImageUploadSettings: { handicap: handicapImageSetting }
    }
  } = data;

  const copyThisYear = () => {
    const thisYear = getValues(`otherFamilies.${index}.thisYear`);
    if (thisYear) {
      setValue(`otherFamilies.${index}.nextYear.dependentTaxLaw`, thisYear?.dependentTaxLaw);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.residenceStatus`, thisYear?.yearlyInfo?.residenceStatus);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.postcode0`, thisYear?.yearlyInfo?.postcode0);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.postcode1`, thisYear?.yearlyInfo?.postcode1);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.prefectureId`, thisYear?.yearlyInfo?.prefectureId);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.city`, thisYear?.yearlyInfo?.city);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.street`, thisYear?.yearlyInfo?.street);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.building`, thisYear?.yearlyInfo?.building);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.addressForeign`, thisYear?.yearlyInfo?.addressForeign);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.isNonResident`, thisYear?.yearlyInfo?.isNonResident);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.isStudyAbroad`, thisYear?.yearlyInfo?.isStudyAbroad);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.remittance`, thisYear?.yearlyInfo?.remittance);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.handicapType`, thisYear?.yearlyInfo?.handicapType);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.handicapDetail`, thisYear?.yearlyInfo?.handicapDetail);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.earnings`, thisYear?.yearlyInfo?.earnings);

      // 画像はコピーできないので空にする
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.relatedToRelativesDocument`, null);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.relatedToRemittanceDocument`, null);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.provingStudyAbroadDocument`, null);
      setValue(`otherFamilies.${index}.nextYear.yearlyInfo.handicapImage`, null);

      trigger();
    }
  };

  return (
    <Section>
      <Section.Header>来年の扶養情報</Section.Header>
      <Section.Body>
        <FormSection>
          {thisYearDependentTaxLaw === 'true' && (
            <ActionButton className={styles.copyThisYear} icon={<Copy size={13} />} onClick={copyThisYear}>
              今年の扶養情報をコピーする
            </ActionButton>
          )}

          <Radio.Container
            label="来年の税の扶養対象"
            note="来年の給与所得者の扶養控除等(異動)申告書に記載されます。"
            required
            error={otherFamilyErrors?.nextYear?.dependentTaxLaw?.message}
          >
            <Radio value="true" {...register(`otherFamilies.${index}.nextYear.dependentTaxLaw`)}>
              対象
            </Radio>
            <Radio value="false" {...register(`otherFamilies.${index}.nextYear.dependentTaxLaw`)}>
              対象外
            </Radio>
          </Radio.Container>

          {nextYearDependentTaxLaw === 'true' && (
            <>
              <Radio.Container label="同居・別居" required>
                <Radio value="same_address" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.residenceStatus`)}>
                  同居
                </Radio>
                <Radio value="different_address" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.residenceStatus`)}>
                  別居（国内）
                </Radio>
                <Radio value="different_and_foreign_address" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.residenceStatus`)}>
                  別居（国外）
                </Radio>
              </Radio.Container>

              {residenceStatus === 'different_address' && <DifferentAddress index={index} />}
              {residenceStatus === 'different_and_foreign_address' && <DifferentAndForeignAddress index={index} />}

              <Radio.Container label="障害者区分" required>
                <Radio value="none" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.handicapType`)}>
                  対象外
                </Radio>
                <Radio value="normal" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.handicapType`)}>
                  一般障害者
                </Radio>
                <Radio value="special" {...register(`otherFamilies.${index}.nextYear.yearlyInfo.handicapType`)}>
                  特別障害者
                </Radio>
              </Radio.Container>

              {(handicapType === 'normal' || handicapType === 'special') && (
                <>
                  <Textarea
                    label="詳細"
                    {...register(`otherFamilies.${index}.nextYear.yearlyInfo.handicapDetail`)}
                    error={otherFamilyErrors?.nextYear?.yearlyInfo?.handicapDetail?.message}
                  />

                  {handicapImageSetting !== 'none' && (
                    <FileField
                      label="障害者手帳画像"
                      name={`otherFamilies.${index}.nextYear.yearlyInfo.handicapImage`}
                      required={handicapImageSetting === 'required'}
                      error={errors.otherFamilies && errors.otherFamilies[index]?.nextYear?.yearlyInfo?.handicapImage?.message}
                    >
                      {handicapImage && (
                        <FileField.FileContainer>
                          <FileField.File
                            file={handicapImage}
                            onRemove={() => setValue(`otherFamilies.${index}.nextYear.yearlyInfo.handicapImage`, null)}
                          />
                          <FilePreview file={handicapImage} />
                        </FileField.FileContainer>
                      )}
                    </FileField>
                  )}
                </>
              )}

              <FormField>
                <FormField.LabelContainer>
                  <Label>所得見積額</Label>
                </FormField.LabelContainer>
                <AmountField
                  error={otherFamilyErrors?.nextYear?.yearlyInfo?.earnings?.message}
                  isError={!!otherFamilyErrors?.nextYear?.yearlyInfo?.earnings?.message}
                  {...register(`otherFamilies.${index}.nextYear.yearlyInfo.earnings`)}
                />
                <FormField.NoteContainer>
                  <Note>
                    税法上の扶養対象については
                    <a className="u-txt-link" href="https://jobcan-lms.zendesk.com/hc/ja/articles/360018787951">
                      こちら
                    </a>
                    をご確認ください。
                    <br />
                    所得が「48万円以下」の場合に税の扶養対象となります。給与収入のみの場合は103万円を超える場合は税の扶養対象に該当しません。
                    <br />
                    年金収入のみの場合は108万円（65歳未満）、158万円（65歳以上）を超える場合は税の扶養対象に該当しません。
                  </Note>
                </FormField.NoteContainer>
              </FormField>
            </>
          )}
        </FormSection>
      </Section.Body>
    </Section>
  );
};
